import jsPDF from 'jspdf';

/**
 * Generic Beslutsbrev PDF Generator
 * 
 * Creates professional PDF for any calculator with Beslutsläge.
 * Pass premium content and calculator-specific data.
 */

// Brand colors
const COLORS = {
  forest800: [34, 84, 61],
  forest700: [21, 87, 54],
  forest600: [22, 101, 52],
  forest50: [240, 253, 244],
  green700: [21, 128, 61],
  blue50: [239, 246, 255],
  blue700: [29, 78, 216],
  red500: [239, 68, 68],
  red50: [254, 242, 242],
  amber500: [245, 158, 11],
  amber50: [254, 249, 195],
  gray700: [55, 65, 81],
  gray500: [107, 114, 128],
  gray400: [156, 163, 175],
  gray300: [209, 213, 219],
  white: [255, 255, 255]
};

const DATA_PERIOD = 'Q2 2026';

/**
 * Generate PDF from premium content
 * @param {object} premiumContent - Generated premium content (lagesbild, osakerheter, etc.)
 * @param {object} options - { calculatorName, arendeText, mainValue, mainUnit }
 */
export const generateGenericBeslutsbrev = (premiumContent, options = {}) => {
  const { 
    calculatorName = 'Skogskalkyl',
    arendeText = 'Beräkning',
    mainValue = '',
    mainUnit = 'kr'
  } = options;
  
  const { lagesbild, osakerheter, beslutsbehov, nastaSteg, beslutsbrev } = premiumContent || {};
  
  const doc = new jsPDF();
  
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - (margin * 2);
  const maxY = pageHeight - 25;
  let y = 20;
  let pageNum = 1;

  const checkPageBreak = (needed = 20) => {
    if (y + needed > maxY) {
      addFooter();
      doc.addPage();
      pageNum++;
      y = 20;
    }
  };

  const addFooter = () => {
    doc.setDrawColor(...COLORS.gray300);
    doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.gray500);
    doc.text('Skogskalkyl.se', margin, pageHeight - 10);
    doc.text(`Sida ${pageNum}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
  };

  const addSection = (title) => {
    checkPageBreak(20);
    doc.setFillColor(...COLORS.forest800);
    doc.roundedRect(margin, y, contentWidth, 8, 1.5, 1.5, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.white);
    doc.text(title, margin + 3, y + 5.5);
    y += 12;
  };

  // ===================
  // HEADER
  // ===================
  doc.setFillColor(...COLORS.forest800);
  doc.rect(0, 0, pageWidth, 32, 'F');
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.white);
  doc.text('Skogskalkyl', margin, 12);
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 220, 200);
  doc.text('Från siffra till bättre beslut', margin, 18);
  
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.white);
  doc.text('BESLUTSBREV', pageWidth - margin, 14, { align: 'right' });
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 220, 200);
  doc.text(`Datum: ${new Date().toLocaleDateString('sv-SE')}  |  Prisdata: ${DATA_PERIOD}`, pageWidth - margin, 22, { align: 'right' });
  
  y = 40;

  // ===================
  // WARNING BOX
  // ===================
  doc.setFillColor(...COLORS.amber50);
  doc.setDrawColor(...COLORS.amber500);
  doc.setLineWidth(0.4);
  doc.roundedRect(margin, y, contentWidth, 10, 2, 2, 'FD');
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(120, 53, 15);
  doc.text('OBS: INDIKATIV UPPSKATTNING – ersätter inte professionell rådgivning.', margin + 3, y + 6);
  y += 15;

  // ===================
  // SUMMARY BOX
  // ===================
  doc.setFillColor(248, 250, 248);
  doc.setDrawColor(...COLORS.forest600);
  doc.setLineWidth(0.4);
  doc.roundedRect(margin, y, contentWidth, 18, 2, 2, 'FD');
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.forest800);
  doc.text(calculatorName, margin + 4, y + 7);
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.gray500);
  doc.text(arendeText, margin + 4, y + 13);
  
  if (mainValue) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.green700);
    doc.text(`${mainValue} ${mainUnit}`, pageWidth - margin - 4, y + 11, { align: 'right' });
  }
  y += 24;

  // ===================
  // 1. LÄGESBILD
  // ===================
  if (lagesbild) {
    addSection('1. Lägesbild');
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.gray700);
    const summaryLines = doc.splitTextToSize(lagesbild.summary || '', contentWidth - 4);
    doc.text(summaryLines, margin + 2, y);
    y += summaryLines.length * 4 + 4;
    
    if (lagesbild.keyPoints) {
      lagesbild.keyPoints.forEach(point => {
        checkPageBreak(5);
        doc.text(`• ${point}`, margin + 4, y);
        y += 4;
      });
    }
    y += 4;
  }

  // ===================
  // 2. OSÄKERHETER
  // ===================
  if (osakerheter) {
    addSection('2. Väsentliga osäkerheter');
    
    osakerheter.factors?.forEach((item) => {
      checkPageBreak(8);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.gray700);
      doc.text(`• ${item.factor}`, margin + 2, y);
      
      // Badge
      const badgeColor = item.impact === 'hög' ? COLORS.red500 : item.impact === 'medel' ? COLORS.amber500 : COLORS.green700;
      doc.setFillColor(...badgeColor);
      doc.roundedRect(margin + 55, y - 3, 12, 4.5, 1, 1, 'F');
      doc.setFontSize(6);
      doc.setTextColor(...COLORS.white);
      doc.setFont('helvetica', 'bold');
      doc.text(item.impact, margin + 57, y - 0.5);
      
      // Description
      doc.setFontSize(7);
      doc.setTextColor(...COLORS.gray500);
      doc.setFont('helvetica', 'normal');
      doc.text(item.desc, margin + 70, y);
      y += 6;
    });
    y += 4;
  }

  // ===================
  // 3. BESLUTSBEHOV
  // ===================
  if (beslutsbehov) {
    addSection('3. Räcker detta underlag?');
    
    checkPageBreak(30);
    
    doc.setFontSize(8);
    beslutsbehov.assessments?.forEach((item, idx) => {
      const xPos = margin + 2;
      const yPos = y + (idx * 5);
      
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.gray700);
      doc.text(`${item.label}:`, xPos, yPos);
      
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(item.answer === 'ja' ? 34 : item.answer === 'nej' ? 200 : 180, 
                       item.answer === 'ja' ? 150 : item.answer === 'nej' ? 50 : 120, 
                       item.answer === 'ja' ? 34 : 50);
      doc.text(item.answer.toUpperCase(), xPos + 50, yPos);
    });
    y += (beslutsbehov.assessments?.length || 0) * 5 + 4;
    
    // Recommendation
    if (beslutsbehov.recommendation) {
      checkPageBreak(15);
      doc.setFillColor(...COLORS.blue50);
      doc.roundedRect(margin, y, contentWidth, 10, 2, 2, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...COLORS.blue700);
      doc.text(beslutsbehov.recommendation, margin + 3, y + 6);
      y += 15;
    }
  }

  // ===================
  // 4. NÄSTA STEG
  // ===================
  if (nastaSteg) {
    addSection('4. Föreslagna nästa steg');
    
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.gray700);
    nastaSteg.steps?.forEach((step) => {
      checkPageBreak(6);
      doc.text(`${step.priority}. ${step.action}`, margin + 2, y);
      y += 5;
    });
    y += 5;
  }

  // ===================
  // FINAL DISCLAIMER
  // ===================
  checkPageBreak(20);
  doc.setFillColor(...COLORS.red50);
  doc.setDrawColor(...COLORS.red500);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin, y, contentWidth, 14, 2, 2, 'FD');
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(150, 30, 30);
  doc.text('VIKTIG INFORMATION', margin + 3, y + 5);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  const discText = doc.splitTextToSize(
    'Detta beslutsbrev är en indikativ uppskattning, inte professionell rådgivning. Kontakta behörig rådgivare för bindande beslut.',
    contentWidth - 6
  );
  doc.text(discText, margin + 3, y + 9);

  addFooter();
  return doc;
};

/**
 * Download PDF
 */
export const downloadGenericBeslutsbrev = (premiumContent, options = {}) => {
  const { fileName = 'Beslutsbrev' } = options;
  const doc = generateGenericBeslutsbrev(premiumContent, options);
  doc.save(`${fileName}_${new Date().toISOString().split('T')[0]}.pdf`);
};

/**
 * Print PDF
 */
export const printGenericBeslutsbrev = () => window.print();

export default { 
  generateGenericBeslutsbrev, 
  downloadGenericBeslutsbrev, 
  printGenericBeslutsbrev 
};
